import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {FAQS_POINTS, KEYWORD_FAQs_ASSESSMENT_ID} from '@axyseo/const/analysis';

/**
 * Represents an assessment that checks if keywords exist in FAQs.
 */
export default class KeywordFAQsAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} [config] The configuration to use.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: KEYWORD_FAQs_ASSESSMENT_ID,
      ctaType: 'fix',
      docUrl: 'https://docs.avada.io/seo-suite-help-center/seo-audit/on-page-seo/faqs-builder',
      priority: 'high',
      fixPosition: 'faqs',
      title: 'Keyword in FAQs',
      content: {
        good: 'Keywords are found in FAQs.',
        improve: '',
        bad: 'No keywords found in FAQs.'
      }
    };

    this.identifier = KEYWORD_FAQs_ASSESSMENT_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Executes the Assessment and returns a result.
   *
   * @param {Paper}       paper       The Paper object to assess.
   * @param {Researcher}  researcher  The Researcher object containing all available researches.
   *
   * @returns {AssessmentResult} The result of the assessment, containing both a score and a descriptive text.
   */
  getResult({paper, researcher}) {
    const calculatedResult = this.calculateResult(paper);

    const assessmentResult = new AssessmentResult({config: this._config});
    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   * Calculates the result of the assessment based on keyword presence in FAQs.
   *
   * @param {Paper} paper The paper to use for the assessment.
   * @returns {Object} The calculated result.
   */
  calculateResult(paper) {
    let status = 'bad'
    const faqs = paper.getFAQs()?.questions || [];
    const keyword = paper.getKeyword();
    const hasKeywords = this.checkKeywordsInFAQs(faqs, [keyword]);
    if(hasKeywords) {
      status = 'good'
    }
    const score = this.getScore(FAQS_POINTS, status);

    return {
      status,
      score
    };
  }

  /**
   * Checks if keywords exist in FAQs
   * @param {Array} faqs
   * @param {Array} keywords
   * @returns {boolean}
   */
  checkKeywordsInFAQs(faqs, keywords) {
    return faqs.some(faq => {
      const question = faq.question.toLowerCase();
      const answer = faq.answer.toLowerCase();
      return keywords.some(keyword => {
        const keywordLower = keyword.toLowerCase();
        return question.includes(keywordLower) || answer.includes(keywordLower);
      });
    });
  }

  /**
   * Checks whether the paper has text.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   *
   * @returns {boolean} True when there is text.
   */
  isApplicable(paper) {
    return true;
  }
}
